import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuCarFront, LuCheck, LuMapPin, LuRefreshCw, LuRoute, LuSettings, LuShieldCheck } from "react-icons/lu";
import { checkLocationPermission, getCurrentLocation, persistLocationState } from "../services/locationService";
import "./LocationPermission.css";

const FEATURES = [
  { id: "drivers", title: "Motoristas próximos", description: "Encontramos os motoristas mais perto de você", icon: "car" },
  { id: "routes", title: "Rotas precisas", description: "Calculamos a melhor rota e o valor exato", icon: "route" },
  { id: "control", title: "Você no controle", description: "Pode desativar a qualquer momento nas configurações", icon: "shield" },
];

export function classifyGeolocationError(error, permissionState) {
  if (error?.code === 1) return permissionState === "denied" ? "blocked" : "denied";
  if (error?.code === 2) return "gps-off";
  return "error";
}

function savePosition(position) {
  persistLocationState("granted", position);
  localStorage.setItem("zunoLocationPrimer", "accepted");
}

function clearPosition() {
  localStorage.removeItem("userLat");
  localStorage.removeItem("userLng");
}

function FeatureIcon({ type }) {
  if (type === "car") {
    return <span className="location-feature-car" aria-hidden="true"><LuCarFront className="location-feature-main-icon" /><LuMapPin className="location-feature-pin-icon" /></span>;
  }
  if (type === "route") return <LuRoute aria-hidden="true" />;
  return <LuShieldCheck aria-hidden="true" />;
}

const FEEDBACK = {
  denied: { title: "Localização não autorizada", description: "Você pode tentar novamente ou continuar sem informar sua localização." },
  blocked: { title: "Permissão bloqueada", description: "Ative a Localização da Zuno nas configurações do dispositivo ou navegador." },
  "gps-off": { title: "Localização indisponível", description: "Verifique se o GPS está ligado e tente novamente." },
  error: { title: "Não foi possível localizar você", description: "Confira sua conexão e tente novamente em alguns instantes." },
  unavailable: { title: "Localização não disponível", description: "Este navegador ou dispositivo não oferece acesso à localização." },
};

function LocationPermission() {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const successTimerRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const isBusy = status === "requesting" || status === "locating";
  const feedback = FEEDBACK[status];

  const finishWithPosition = useCallback((position) => {
    if (!mountedRef.current) return;
    savePosition(position);
    setStatus("success");
    successTimerRef.current = window.setTimeout(() => {
      if (mountedRef.current) navigate("/login", { replace: true });
    }, 450);
  }, [navigate]);

  const handlePositionError = useCallback(async (error) => {
    clearPosition();
    const permissionState = await checkLocationPermission();
    persistLocationState(permissionState === "granted" ? "denied" : permissionState);
    if (mountedRef.current) {
      if (error?.reason === "timeout") setStatus("error");
      else if (error?.reason === "gps-off" || error?.reason === "restricted") setStatus(error.reason === "restricted" ? "blocked" : error.reason);
      else setStatus(classifyGeolocationError(error?.cause || error, permissionState));
    }
  }, []);

  const requestCurrentPosition = useCallback(async (nextStatus = "requesting") => {
    setStatus(nextStatus);
    try {
      finishWithPosition(await getCurrentLocation({ requestPermission: nextStatus === "requesting" }));
    } catch (error) {
      await handlePositionError(error);
    }
  }, [finishWithPosition, handlePositionError]);

  useEffect(() => {
    mountedRef.current = true;
    const inspectExistingPermission = async () => {
      const permissionState = await checkLocationPermission();
      if (!mountedRef.current) return;
      if (permissionState === "granted") requestCurrentPosition("locating");
      else if (permissionState === "denied" || permissionState === "restricted") setStatus("blocked");
      else if (permissionState === "gps-off" || permissionState === "unavailable") setStatus(permissionState);
    };
    inspectExistingPermission();
    return () => {
      mountedRef.current = false;
      if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
    };
  }, [requestCurrentPosition]);

  const continueWithoutLocation = () => {
    clearPosition();
    persistLocationState("denied");
    localStorage.setItem("zunoLocationPrimer", "skipped");
    navigate("/login", { replace: true });
  };

  const buttonLabel = status === "locating" ? "Atualizando localização..." : status === "requesting" ? "Aguardando permissão..." : "Permitir localização";

  return (
    <main className={`location-permission location-state-${status}`}>
      <div className="location-ambient location-ambient-one" aria-hidden="true" />
      <div className="location-ambient location-ambient-two" aria-hidden="true" />
      <section className="location-permission-panel" aria-labelledby="location-title">
        <div className="location-radar" aria-hidden="true">
          <span className="location-orbit location-orbit-static location-orbit-one" />
          <span className="location-orbit location-orbit-static location-orbit-two" />
          <span className="location-radar-wave location-radar-wave-one" />
          <span className="location-radar-wave location-radar-wave-two" />
          <span className="location-radar-wave location-radar-wave-three" />
          <span className="location-radar-platform" />
          <span className="location-radar-core">{status === "success" ? <LuCheck /> : <LuMapPin />}</span>
        </div>

        <header className="location-copy">
          <h1 id="location-title">Permitir sua localização</h1>
          <p>Para encontrar motoristas próximos e calcular rotas com precisão, o Zuno precisa acessar sua localização.</p>
        </header>

        <div className="location-features">
          {FEATURES.map((feature) => (
            <article className="location-feature-card" key={feature.id}>
              <div className="location-feature-icon"><FeatureIcon type={feature.icon} /></div>
              <div className="location-feature-copy"><h2>{feature.title}</h2><p>{feature.description}</p></div>
            </article>
          ))}
        </div>

        <div className="location-actions">
          {feedback && (
            <div className={`location-feedback location-feedback-${status}`} role="status" aria-live="polite">
              <LuSettings aria-hidden="true" />
              <div><strong>{feedback.title}</strong><span>{feedback.description}</span></div>
            </div>
          )}
          {status === "success" ? (
            <div className="location-success" role="status" aria-live="polite"><LuCheck aria-hidden="true" />Localização autorizada</div>
          ) : (
            <button className="location-primary-button" type="button" onClick={() => requestCurrentPosition("requesting")} disabled={isBusy || status === "unavailable"}>
              {isBusy ? <span className="location-spinner" aria-hidden="true" /> : feedback ? <LuRefreshCw aria-hidden="true" /> : <LuMapPin aria-hidden="true" />}
              <span>{feedback ? "Tentar novamente" : buttonLabel}</span>
            </button>
          )}
          {status !== "success" && <button className="location-secondary-button" type="button" onClick={continueWithoutLocation}>{feedback ? "Continuar sem localização" : "Agora não"}</button>}
        </div>
      </section>
    </main>
  );
}

export default LocationPermission;
